import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAPI from "api/hooks/useAPI";
import { Toast } from "components/Toast";
import { debounce } from "lodash";
import { useState } from "react";
import { GRID_CONFIG, WIDGET_DEFINITIONS } from "../widgetDefinitions";

const DEFAULT_WIDGETS = WIDGET_DEFINITIONS;

const buildLayouts = (widgets, existingLayouts) => {
  const saved = new Map(
    (existingLayouts?.lg || []).map((layout) => [layout.i, layout])
  );

  const visibleWidgets = widgets.filter((w) => w.isVisible);

  const layoutsByBreakpoint = {};
  Object.keys(GRID_CONFIG.breakpoints).forEach((breakpoint) => {
    layoutsByBreakpoint[breakpoint] = visibleWidgets.map((widget) => {
      const defaultLayout = widget.defaultLayout[breakpoint];
      const savedLayout = saved.get(widget.id);

      return savedLayout
        ? { ...defaultLayout, ...savedLayout, i: widget.id }
        : { ...defaultLayout, i: widget.id };
    });
  });

  return layoutsByBreakpoint;
};

export const useInternalDashboard = () => {
  const {
    saveInternalDashboardLayout,
    loadInternalDashboardLayout,
    trackEvent,
  } = useAPI();
  const queryClient = useQueryClient();

  const {
    data: layoutData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["internal-dashboard-layout"],
    queryFn: loadInternalDashboardLayout,
    select: (response) => {
      if (response?.data) {
        const parsed =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;

        if (parsed && Object.keys(parsed).length > 0 && parsed.widgets) {
          const savedWidgets = parsed.widgets;

          const mergedWidgets = DEFAULT_WIDGETS.map((defaultWidget) => {
            const savedWidget = savedWidgets.find(
              (w) => w.id === defaultWidget.id
            );
            if (savedWidget) {
              return {
                ...defaultWidget,
                ...savedWidget,
                isRemovable: defaultWidget.isRemovable,
                isVisible:
                  savedWidget.isVisible !== undefined
                    ? savedWidget.isVisible
                    : defaultWidget.isVisible,
              };
            }
            return defaultWidget;
          });

          return {
            widgets: mergedWidgets,
            layouts: parsed.layouts || {},
          };
        }
      }
      return {
        widgets: DEFAULT_WIDGETS,
        layouts: {},
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);

  // Sync widgets with layoutData when it changes
  if (
    layoutData?.widgets &&
    layoutData.widgets.length > 0 &&
    widgets === DEFAULT_WIDGETS
  ) {
    setWidgets(layoutData.widgets);
  }

  const {
    mutate: saveLayoutMutation,
    isPending: isSaving,
    isSuccess: isSaved,
  } = useMutation({
    mutationFn: (data) => {
      return saveInternalDashboardLayout(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["internal-dashboard-layout"],
      });
    },
    onError: () => {
      Toast.error("Error saving dashboard layout");
    },
  });

  const layouts = layoutData?.layouts || {};
  const generateLayouts = buildLayouts(widgets, layouts);

  const debouncedSave = debounce((layoutData) => {
    trackEvent("User re-ordered internal dashboard widgets");
    saveLayoutMutation(layoutData);
  }, 1000);

  const onLayoutChange = (currentLayout, allLayouts) => {
    const updatedLayoutData = {
      layouts: { lg: allLayouts.lg },
      widgets,
    };

    debouncedSave(updatedLayoutData);
  };

  const onToggleWidget = (widgetId) => {
    const updatedWidgets = widgets.map((widget) =>
      widget.id === widgetId
        ? { ...widget, isVisible: !widget.isVisible }
        : widget
    );
    setWidgets(updatedWidgets);

    const updatedLayoutData = {
      layouts,
      widgets: updatedWidgets,
    };

    trackEvent("User toggled internal dashboard widget visibility");
    saveLayoutMutation(updatedLayoutData);
  };

  return {
    mounted: true,
    widgets,
    layouts,
    generateLayouts,
    isLoading,
    isError,
    isSaving,
    isSaved,
    onLayoutChange,
    onToggleWidget,
    gridConfig: GRID_CONFIG,
  };
};
